import { useState, useEffect, useRef } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase } from "@/lib/supabaseClient";

const CATEGORY_LIST = [
  { id: 1, name: "Ephemera & Papers" },
  { id: 2, name: "Washi Tape" },
  { id: 3, name: "Stamps & Ink" },
  { id: 4, name: "Stickers & Die Cuts" },
  { id: 5, name: "Snail Mail Kits" },
  { id: 6, name: "Journal Kits & Bundles" },
  { id: 7, name: "Fabric & Textiles" },
  { id: 8, name: "Tools & Supplies" },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

type FormState = {
  creatorName: string;
  businessName: string;
  email: string;
  website: string;
  location: string;
  type: string;
  specialty: string;
  categories: string[];
  bio: string;
  instagram: string;
  etsy: string;
  howHeard: string;
};

const initialForm: FormState = {
  creatorName: "",
  businessName: "",
  email: "",
  website: "",
  location: "",
  type: "",
  specialty: "",
  categories: [],
  bio: "",
  instagram: "",
  etsy: "",
  howHeard: "",
};

export default function SubmitPage() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = "Submit Your Creator Info — Join the Junk Journal Directory | Junk & Post";
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", "Submit your junk journal or snail mail creator info to be listed in the Junk & Post directory. Connect with thousands of paper crafting enthusiasts looking for handmade ephemera and supplies.");
    setMeta("og:title", "Submit Your Creator Info — Join the Junk Journal Directory | Junk & Post", true);
    setMeta("og:description", "Submit your junk journal or snail mail creator info to be listed in the Junk & Post directory.", true);
    setMeta("og:url", `${SITE_URL}/submit`, true);

    const existing = document.getElementById("schema-submit");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "schema-submit";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Submit Your Creator Info — Junk & Post Directory",
      "description": "Submit your junk journal or snail mail creator info to be listed in the Junk & Post directory. Connect with thousands of paper crafting enthusiasts.",
      "url": `${SITE_URL}/submit`,
      "isPartOf": { "@type": "WebSite", "url": SITE_URL, "name": "Junk & Post" }
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById("schema-submit")?.remove();
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleCategory = (catName: string) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.includes(catName)
        ? prev.categories.filter((c) => c !== catName)
        : [...prev.categories, catName],
    }));
  };

  const handleImageChange = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please upload a valid image file (JPG, PNG, or WebP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB.");
      return;
    }
    setError("");
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageChange(file);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.bio.length > 500) {
      setError("Bio must be 500 characters or less.");
      return;
    }
    if (!form.type) {
      setError("Please select a creator type.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      let imageUrl: string | null = null;

      if (imageFile) {
        const ext = imageFile.name.split(".").pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("creator-images")
          .upload(fileName, imageFile, { contentType: imageFile.type, upsert: false });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage
          .from("creator-images")
          .getPublicUrl(fileName);
        imageUrl = urlData.publicUrl;
      }

      const { error: sbError } = await supabase.from("creators").insert({
        creator_name: form.creatorName,
        business_name: form.businessName,
        email: form.email,
        website: form.website || null,
        location: form.location || null,
        type: form.type,
        specialty: form.specialty,
        categories: form.categories.join(", ") || null,
        bio: form.bio || null,
        instagram: form.instagram || null,
        etsy: form.etsy || null,
        how_heard: form.howHeard || null,
        image_url: imageUrl,
        featured: false,
        approved: false,
        status: "pending",
      });
      if (sbError) throw sbError;
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-12 bg-[#3a2410]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#f0c87a] text-xs font-semibold uppercase tracking-widest mb-4">
            <i className="ri-user-add-line"></i>
            Join the Directory
          </div>
          <h1
            className="text-4xl font-bold text-white mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Submit Your Creator Info
          </h1>
          <p className="text-[#c9a87c] max-w-xl mx-auto">
            Get listed in the Junk &amp; Post directory and connect with thousands of enthusiasts
            actively searching for creators like you.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {submitted ? (
          <div className="bg-white rounded-2xl border border-[#c9a87c]/40 p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 rounded-full bg-[#f5ede0] text-[#8b5e3c]">
              <i className="ri-checkbox-circle-line text-4xl"></i>
            </div>
            <h2
              className="text-3xl font-bold text-[#2c1e0f] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Submission Received!
            </h2>
            <p className="text-[#7a6555] mb-6 max-w-md mx-auto">
              Thank you for submitting your information! Our team will review your listing and get
              back to you within 3–5 business days.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm(initialForm);
                setImageFile(null);
                setImagePreview(null);
              }}
              className="px-6 py-3 rounded-full bg-[#8b5e3c] text-white font-semibold text-sm hover:bg-[#7a5234] transition-colors cursor-pointer whitespace-nowrap"
            >
              Submit Another Listing
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-[#ede5d8] p-8 flex flex-col gap-6"
          >
            {/* Section: Basic Info */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Basic Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Your Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="creatorName"
                    value={form.creatorName}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Business / Shop Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={form.businessName}
                    onChange={handleChange}
                    placeholder="Rosie Inkwell Studio"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="hello@yourstudio.com"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Location (City, State/Country)
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="Portland, OR"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
              </div>
            </div>

            {/* Section: Creator Type */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Creator Type
              </h2>
              <div className="flex flex-wrap gap-3">
                {["Individual Creator", "Small Business", "Company / Brand", "Collective"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, type: t })}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      form.type === t
                        ? "bg-[#8b5e3c] text-white"
                        : "bg-[#f5ede0] text-[#8b5e3c] hover:bg-[#ede0cc]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Section: Products & Specialty */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Products &amp; Specialty
              </h2>
              <div className="mb-4">
                <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                  Your Specialty / Tagline <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="specialty"
                  value={form.specialty}
                  onChange={handleChange}
                  placeholder="e.g. Vintage Ephemera &amp; Washi Tape Designer"
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-2 block">
                  Product Categories (select all that apply)
                </label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_LIST.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => toggleCategory(cat.name)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        form.categories.includes(cat.name)
                          ? "bg-[#8b5e3c] text-white"
                          : "bg-[#f5ede0] text-[#8b5e3c] hover:bg-[#ede0cc]"
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Section: About You */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                About You
              </h2>

              {/* Profile Photo Upload */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                  Profile Photo
                  <span className="ml-2 text-[#9a8a78] normal-case font-normal tracking-normal">
                    (optional — JPG, PNG or WebP, max 5MB)
                  </span>
                </label>
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="w-20 h-20 flex-shrink-0 rounded-full overflow-hidden border-2 border-[#ede5d8] bg-[#f5ede0] flex items-center justify-center">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <i className="ri-user-3-line text-2xl text-[#c9a87c]"></i>
                    )}
                  </div>

                  {/* Drop zone */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex-1 flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-xl border-2 border-dashed transition-colors cursor-pointer ${
                      dragOver
                        ? "border-[#8b5e3c] bg-[#f5ede0]"
                        : "border-[#ede5d8] hover:border-[#c9a87c] hover:bg-[#faf8f4]"
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center text-[#c9a87c]">
                      <i className="ri-upload-cloud-2-line text-2xl"></i>
                    </div>
                    {imageFile ? (
                      <div className="text-center">
                        <p className="text-sm font-medium text-[#3a2e1e]">{imageFile.name}</p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageFile(null);
                            setImagePreview(null);
                            if (fileInputRef.current) fileInputRef.current.value = "";
                          }}
                          className="text-xs text-[#8b5e3c] underline mt-1 cursor-pointer"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className="text-sm text-[#7a6555]">
                          <span className="font-semibold text-[#8b5e3c]">Click to upload</span> or drag &amp; drop
                        </p>
                        <p className="text-xs text-[#9a8a78] mt-0.5">Recommended: square image, at least 400×400px</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageChange(file);
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                  Short Bio <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="bio"
                  value={form.bio}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) handleChange(e);
                  }}
                  placeholder="Tell the community about yourself, your craft, and what makes your products special..."
                  rows={4}
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c] resize-none"
                />
                <p className="text-xs text-[#9a8a78] mt-1 text-right">{form.bio.length}/500</p>
              </div>
            </div>

            {/* Section: Links */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Links &amp; Social Media
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    placeholder="https://yourstudio.com"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Etsy Shop URL
                  </label>
                  <input
                    type="url"
                    name="etsy"
                    value={form.etsy}
                    onChange={handleChange}
                    placeholder="https://etsy.com/shop/yourshop"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Instagram Handle
                  </label>
                  <input
                    type="text"
                    name="instagram"
                    value={form.instagram}
                    onChange={handleChange}
                    placeholder="@yourstudio"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    How did you hear about us?
                  </label>
                  <select
                    name="howHeard"
                    value={form.howHeard}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] bg-white focus:outline-none focus:border-[#c9a87c] cursor-pointer"
                  >
                    <option value="">Select an option</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Pinterest">Pinterest</option>
                    <option value="Word of mouth">Word of mouth</option>
                    <option value="Google search">Google search</option>
                    <option value="Community forum">Community forum</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3.5 rounded-full bg-[#8b5e3c] text-white font-semibold text-base hover:bg-[#7a5234] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {submitting ? "Uploading & Submitting..." : "Submit My Information"}
              </button>
            </div>
            <p className="text-xs text-[#9a8a78] text-center">
              By submitting, you agree to our listing guidelines. We review all submissions within
              3–5 business days.
            </p>
          </form>
        )}
      </div>

      <Footer />
    </div>
  );
}
